import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar
} from '@mui/material';
import { apiClient } from '../../lib/apiClient';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../../components/common/LoadingOverlay';

function CreateChurch({ user }) {
  const [formData, setFormData] = useState({
    name: '',
    address: {
      address: '',
      city: '',
      state: '',
      country: 'India',
      pincode: ''
    }
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [field]: value
      });
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError('');
    setLogoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadingLogo(true);
    setError('');

    // Validate logo is uploaded
    if (!logoFile) {
      setError('Please upload a church logo');
      setLoading(false);
      setUploadingLogo(false);
      return;
    }

    try {
      // First create the church
      const church = await apiClient.createChurch(
        formData.name,
        formData.address,
        user.id,
        [] // No initial admins
      );

      // Then upload logo (required)
      if (church.id) {
        await apiClient.uploadChurchLogo(church.id, logoFile, user.id);
      }

      navigate('/super-admin/churches');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setUploadingLogo(false);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <LoadingOverlay open={uploadingLogo} message="Uploading logo and creating church..." />
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Create Church
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, maxWidth: { xs: '100%', md: 800 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Logo Upload */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={logoPreview}
                sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 } }}
              >
                {formData.name?.charAt(0) || 'C'}
              </Avatar>
              <Button variant="outlined" component="label" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                Upload Logo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoChange}
                  required
                />
              </Button>
              {!logoFile && (
                <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                  Logo is required
                </Typography>
              )}
            </Box>

            <TextField
              fullWidth
              label="Church Name"
              required
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            <TextField
              fullWidth
              label="Address"
              value={formData.address.address}
              onChange={(e) => handleChange('address.address', e.target.value)}
            />
            <TextField
              fullWidth
              label="City"
              value={formData.address.city}
              onChange={(e) => handleChange('address.city', e.target.value)}
            />
            <TextField
              fullWidth
              label="State"
              value={formData.address.state}
              onChange={(e) => handleChange('address.state', e.target.value)}
            />
            <TextField
              fullWidth
              label="Country"
              value={formData.address.country}
              onChange={(e) => handleChange('address.country', e.target.value)}
            />
            <TextField
              fullWidth
              label="Pincode"
              value={formData.address.pincode}
              onChange={(e) => handleChange('address.pincode', e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !logoFile}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Church'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/super-admin/churches')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

export default CreateChurch;

